/**
 * The reader screen's words.
 *
 * `en` is the source, and `fr` is typed as covering every one of its keys, so
 * a forgotten translation is a build error rather than an English word that
 * quietly shows up in a French interface.
 *
 * Several surfaces share this file, and the prefix says which: `reader` is the
 * shell and its header, `player` the transport, `chapter` the side panel,
 * `prefs` the typography popover, `page` the scanned page, `edition` the
 * crossing between two languages of one book, `shortcut` the key list.
 */

const en = {
  // The shell: header, error state, chapter footer.
  readerBackToLibrary: "Back to the library",
  readerOpenFailed: "This book could not be opened",
  readerViewGroup: "View",
  readerViewText: "Translated text",
  readerViewPage: "Original page",
  readerNoDocument: "This book has no original document",
  readerPreferences: "Reading preferences",
  readerChapter: "Chapter {n}",
  readerChapterOf: "of {total}",
  readerPrevious: "Previous",
  readerNext: "Next",
  readerPageOne: "{count} page",
  readerPageMany: "{count} pages",
  readerShortcutsDialog: "Keyboard shortcuts",
  readerKeyboard: "Keyboard",

  // The transport bar.
  playerPlay: "Play",
  playerPause: "Pause",
  playerSkipBack: "Back {seconds}s",
  playerSkipForward: "Forward {seconds}s",
  playerPosition: "Position in the book",
  playerPositionValue: "{time} of {total}",
  playerSpeed: "Playback speed",
  playerSpeedCurrent: "Playback speed, currently {rate} times",
  playerNoSync: "audio without synchronisation",
  playerNoSyncHint:
    "This book has audio but no sentence alignment yet, so nothing is highlighted.",
  playerSleepRemaining: "Sleep timer, {time} left",
  playerSleepArmed: "Sleep timer on",
  playerMore: "More",

  // The sleep timer menu.
  sleepTitle: "Sleep timer",
  sleepOpen: "Sleep timer",
  sleepRemaining: "Sleep timer, {time} left",
  sleepClose: "Close sleep timer",
  sleepStoppingIn: "Stopping in",
  sleepUntilChapterEnds: "Until this chapter ends",
  sleepChaptersLeft: "{n} chapters left",
  sleepLastChapter: "Last chapter",
  sleepAddMore: "Add more time",
  sleepStop: "Stop the timer",
  sleepStopAfter: "Stop after",
  sleepMinutes: "{minutes} min",
  sleepEndOfChapter: "End of chapter",
  sleepChaptersGroup: "Chapters",
  sleepChapterCountOne: "{count} chapter",
  sleepChapterCountMany: "{count} chapters",
  sleepOff: "Off",

  // Audio settings: skip length, speed for this book, sound processing.
  audioPrefsTitle: "Audio settings",
  audioPrefsOpen: "Audio settings, speed {rate}x",
  audioPrefsClose: "Close",
  audioPrefsSkipBack: "Rewind",
  audioPrefsSkipForward: "Fast forward",
  audioPrefsSpeed: "Speed for this book",
  audioPrefsProcessing: "Sound processing",
  audioPrefsBoost: "Voice boost",
  audioPrefsBoostTitle:
    "Compresses the track so a quiet narrator stays audible over background noise.",
  audioPrefsNormalize: "Normalize",
  audioPrefsNormalizeTitle: "Levels out loudness swings between chapters and between books.",
  audioPrefsTrimSilence: "Trim silences",
  audioPrefsTrimSilenceTitle:
    "Shortens long silent gaps in the recording instead of playing through them.",
  audioPrefsSeconds: "{value}s",
  audioPrefsSpeedValue: "{value}x",

  // Bookmarks: timestamped spots in the audio.
  bookmarkTitle: "Bookmarks",
  bookmarkOpen: "Bookmarks",
  bookmarkClose: "Close bookmarks",
  bookmarkAddHere: "Bookmark this spot",
  bookmarkEmpty:
    'No bookmarks yet. Tap "Bookmark this spot" while listening to mark your place.',
  bookmarkNotePlaceholder: "Note (optional)",
  bookmarkSave: "Save",
  bookmarkCancel: "Cancel",
  bookmarkEdit: "Edit note",
  bookmarkDelete: "Delete bookmark",
  bookmarkDeleteConfirm: "Tap again to delete",

  // Chapters and search inside the book.
  chapterNavLabel: "Chapters and search",
  chapterSearch: "Search in this book",
  chapterClose: "Close chapters",
  chapterSearching: "Searching",
  chapterNothingFound: "Nothing found",
  chapterPageShort: "p. {page}",
  chapterWordsShort: "{count} w",

  // Typography preferences.
  prefsTitle: "Reading",
  prefsClose: "Close preferences",
  prefsTextSize: "Text size",
  prefsPixels: "{n} px",
  prefsLineHeight: "Line height",
  prefsLeadTight: "Tight",
  prefsLeadNormal: "Normal",
  prefsLeadLoose: "Airy",
  prefsMeasure: "Measure",
  prefsMeasureNarrow: "Narrow",
  prefsMeasureNormal: "Normal",
  prefsMeasureWide: "Wide",
  prefsMeasureChars: "About {n} characters a line",
  prefsTypeface: "Typeface",
  prefsSerif: "Serif",
  prefsSans: "Sans",
  prefsReset: "Reset to defaults",

  // Keeping this book on the device. A section of the reading panel, not a
  // header button: see reader/ReadingPrefs.tsx.
  offlineKeepLabel: "Keep this book offline",
  offlineSavedLabel: "Kept offline",
  offlineTitle: "Offline reading",
  offlineAbsentHint:
    "This book reads online today. Keeping it here means it still opens with no network at all.",
  offlineDownloading: "Downloading, {percent}%",
  offlineSavedHint: "This book is on this device: {size}. It opens with no network at all.",
  offlineFailed: "The download stopped before the whole book was here. Nothing was kept.",
  offlineUsage: "{used} used, about {free} free",
  offlineRemove: "Remove",
  offlineRetry: "Try again",
  offlineClose: "Close",

  // The scanned page.
  pageNumber: "Page {n}",
  pageNumberOf: "Page {n} of {total}",
  pagePrevious: "Previous page",
  pageNext: "Next page",
  pageGoTo: "Go to page",
  pageZoomOut: "Zoom out",
  pageZoomIn: "Zoom in",
  pageFit: "Fit page to the window",
  pageMissingTitle: "No original document",
  pageMissingBody: "This book was added as text and audio only.",
  pageUnsupportedTitle: "The original is not a PDF",
  pageUnsupportedBody:
    "The page view renders PDF scans. This book ships an EPUB, which has no fixed pages.",
  pageErrorTitle: "The document could not be opened",
  pageErrorBody: "The file may be damaged or still uploading.",

  // Crossing to another language of the same book.
  editionGroup: "Language of this book",
  editionReading: "Reading in {title}",
  editionSwitchNarrated: "Switch to {title}, narrated",
  editionSwitchTextOnly: "Switch to {title}, text only",
  // Below 400px the two-language group has no room left; one button stands
  // for it, showing the language it would cross to. The narration mark is
  // gone from view at that width, so the aria-label and title below are its
  // only trace: see EditionSwitch.tsx.
  editionSwitchCompact: "Read the {lang} edition",

  // What each key does. The keys themselves are printed on the keyboard and
  // stay as they are; only these descriptions are translated.
  shortcutPlayPause: "Play or pause",
  shortcutNudge: "Back or forward",
  shortcutSentence: "Previous or next sentence",
  shortcutRate: "Slower or faster",
  shortcutView: "Switch text and page",
  shortcutChapters: "Chapters",
  shortcutHelp: "This list",
  shortcutClose: "Close",
} as const;

const fr: Record<keyof typeof en, string> = {
  readerBackToLibrary: "Retour à la bibliothèque",
  readerOpenFailed: "Impossible d'ouvrir ce livre",
  readerViewGroup: "Vue",
  readerViewText: "Texte traduit",
  readerViewPage: "Page originale",
  readerNoDocument: "Ce livre n'a pas de document original",
  readerPreferences: "Préférences de lecture",
  readerChapter: "Chapitre {n}",
  readerChapterOf: "sur {total}",
  readerPrevious: "Précédent",
  readerNext: "Suivant",
  readerPageOne: "{count} page",
  readerPageMany: "{count} pages",
  readerShortcutsDialog: "Raccourcis clavier",
  readerKeyboard: "Clavier",

  playerPlay: "Lecture",
  playerPause: "Pause",
  playerSkipBack: "Reculer de {seconds} s",
  playerSkipForward: "Avancer de {seconds} s",
  playerPosition: "Position dans le livre",
  playerPositionValue: "{time} sur {total}",
  playerSpeed: "Vitesse de lecture",
  playerSpeedCurrent: "Vitesse de lecture, actuellement {rate} fois",
  playerNoSync: "audio non synchronisé",
  playerNoSyncHint:
    "Ce livre a une narration, mais ses phrases ne sont pas encore alignées : rien ne s'allume.",
  playerSleepRemaining: "Minuterie de sommeil, {time} restant",
  playerSleepArmed: "Minuterie de sommeil activée",
  playerMore: "Plus",

  sleepTitle: "Minuterie de sommeil",
  sleepOpen: "Minuterie",
  sleepRemaining: "Minuterie, {time} restantes",
  sleepClose: "Fermer la minuterie",
  sleepStoppingIn: "Arrêt dans",
  sleepUntilChapterEnds: "Jusqu'à la fin du chapitre",
  sleepChaptersLeft: "{n} chapitres restants",
  sleepLastChapter: "Dernier chapitre",
  sleepAddMore: "Ajouter du temps",
  sleepStop: "Arrêter la minuterie",
  sleepStopAfter: "Arrêter après",
  sleepMinutes: "{minutes} min",
  sleepEndOfChapter: "Fin du chapitre",
  sleepChaptersGroup: "Chapitres",
  sleepChapterCountOne: "{count} chapitre",
  sleepChapterCountMany: "{count} chapitres",
  sleepOff: "Désactivée",

  audioPrefsTitle: "Réglages audio",
  audioPrefsOpen: "Réglages audio, vitesse {rate}x",
  audioPrefsClose: "Fermer",
  audioPrefsSkipBack: "Recul",
  audioPrefsSkipForward: "Avance",
  audioPrefsSpeed: "Vitesse de ce livre",
  audioPrefsProcessing: "Traitement du son",
  audioPrefsBoost: "Boost de voix",
  audioPrefsBoostTitle:
    "Compresse la piste pour qu'une voix faible reste audible malgré le bruit ambiant.",
  audioPrefsNormalize: "Normalisation",
  audioPrefsNormalizeTitle: "Aplanit les écarts de volume entre les chapitres et entre les livres.",
  audioPrefsTrimSilence: "Retrait des silences",
  audioPrefsTrimSilenceTitle: "Raccourcit les longs blancs de l'enregistrement au lieu de les jouer.",
  audioPrefsSeconds: "{value} s",
  audioPrefsSpeedValue: "{value}x",

  bookmarkTitle: "Marque-pages",
  bookmarkOpen: "Marque-pages",
  bookmarkClose: "Fermer les marque-pages",
  bookmarkAddHere: "Marquer cette position",
  bookmarkEmpty:
    "Aucun marque-page. Touchez « Marquer cette position » pendant l'écoute pour repérer votre place.",
  bookmarkNotePlaceholder: "Note (facultative)",
  bookmarkSave: "Enregistrer",
  bookmarkCancel: "Annuler",
  bookmarkEdit: "Modifier la note",
  bookmarkDelete: "Supprimer le marque-page",
  bookmarkDeleteConfirm: "Touchez de nouveau pour supprimer",

  chapterNavLabel: "Chapitres et recherche",
  chapterSearch: "Rechercher dans ce livre",
  chapterClose: "Fermer les chapitres",
  chapterSearching: "Recherche en cours",
  chapterNothingFound: "Aucun résultat",
  chapterPageShort: "p. {page}",
  chapterWordsShort: "{count} m",

  prefsTitle: "Lecture",
  prefsClose: "Fermer les préférences",
  prefsTextSize: "Taille du texte",
  prefsPixels: "{n} px",
  prefsLineHeight: "Interligne",
  prefsLeadTight: "Serré",
  prefsLeadNormal: "Normal",
  prefsLeadLoose: "Aéré",
  prefsMeasure: "Largeur de colonne",
  prefsMeasureNarrow: "Étroite",
  prefsMeasureNormal: "Normale",
  prefsMeasureWide: "Large",
  prefsMeasureChars: "Environ {n} caractères par ligne",
  prefsTypeface: "Police",
  prefsSerif: "Serif",
  prefsSans: "Sans",
  prefsReset: "Revenir aux réglages d'origine",

  offlineKeepLabel: "Garder ce livre hors ligne",
  offlineSavedLabel: "Gardé hors ligne",
  offlineTitle: "Lecture hors ligne",
  offlineAbsentHint:
    "Ce livre se lit en ligne pour l'instant. Le garder ici lui permet de s'ouvrir sans aucun réseau.",
  offlineDownloading: "Téléchargement, {percent} %",
  offlineSavedHint: "Ce livre est sur cet appareil : {size}. Il s'ouvre sans aucun réseau.",
  offlineFailed: "Le téléchargement s'est arrêté avant d'avoir tout le livre. Rien n'a été gardé.",
  offlineUsage: "{used} utilisés, environ {free} libres",
  offlineRemove: "Supprimer",
  offlineRetry: "Réessayer",
  offlineClose: "Fermer",

  pageNumber: "Page {n}",
  pageNumberOf: "Page {n} sur {total}",
  pagePrevious: "Page précédente",
  pageNext: "Page suivante",
  pageGoTo: "Aller à la page",
  pageZoomOut: "Zoom arrière",
  pageZoomIn: "Zoom avant",
  pageFit: "Ajuster la page",
  pageMissingTitle: "Aucun document original",
  pageMissingBody: "Ce livre a été ajouté en texte et en audio seulement.",
  pageUnsupportedTitle: "L'original n'est pas un PDF",
  pageUnsupportedBody:
    "La vue page affiche des scans PDF. Ce livre est un EPUB, qui n'a pas de pages fixes.",
  pageErrorTitle: "Impossible d'ouvrir le document",
  pageErrorBody: "Le fichier est peut-être abîmé, ou son envoi n'est pas terminé.",

  editionGroup: "Langue de ce livre",
  editionReading: "Vous lisez {title}",
  editionSwitchNarrated: "Passer à {title}, avec narration",
  editionSwitchTextOnly: "Passer à {title}, texte seul",
  editionSwitchCompact: "Lire l'édition en {lang}",

  shortcutPlayPause: "Lire ou mettre en pause",
  shortcutNudge: "Reculer ou avancer",
  shortcutSentence: "Phrase précédente ou suivante",
  shortcutRate: "Plus lent ou plus rapide",
  shortcutView: "Basculer entre texte et page",
  shortcutChapters: "Chapitres",
  shortcutHelp: "Cette liste",
  shortcutClose: "Fermer",
};

export const reader = { en, fr };
